import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { MainLayout } from '../../components/layout/MainLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Progress } from '../../components/ui/progress';
import { Skeleton } from '../../components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import useContentProtection from '../../hooks/useContentProtection';
import { 
  Globe2, 
  Award, 
  ChevronRight, 
  CheckCircle2, 
  Lock,
  Play,
  BookOpen,
  Clock,
  Trophy,
  Flag,
  Shield,
  FileText,
  Sparkles
} from 'lucide-react';

const RPATGlobalModules = () => {
  const { api, user } = useAuth();
  const navigate = useNavigate();
  
  useContentProtection(true);
  
  const [loading, setLoading] = useState(true);
  const [modules, setModules] = useState([]);
  const [badges, setBadges] = useState([]);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('modules');

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const [modulesRes, badgesRes] = await Promise.all([
        api.get('/rpat/global/modules'),
        api.get('/rpat/global/badges')
      ]);
      setModules(modulesRes.data.modules || []);
      setBadges(badgesRes.data.badges || []);
    } catch (err) {
      console.error('Failed to fetch global modules:', err);
      setError('Failed to load global modules');
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const getRegionGradient = (region) => {
    switch (region) {
      case 'us': return 'from-blue-600 via-white to-red-600';
      case 'uk': return 'from-blue-700 via-red-600 to-white';
      case 'au': return 'from-blue-600 via-white to-yellow-500';
      case 'ca': return 'from-red-600 via-white to-red-600';
      case 'de': return 'from-black via-red-600 to-yellow-500';
      default: return 'from-cyan-500 to-blue-600';
    }
  };

  const getRegionBg = (region) => {
    switch (region) {
      case 'us': return 'bg-gradient-to-br from-blue-50 to-red-50 border-blue-200';
      case 'uk': return 'bg-gradient-to-br from-blue-50 to-red-50 border-blue-200';
      case 'au': return 'bg-gradient-to-br from-blue-50 to-yellow-50 border-blue-200';
      case 'ca': return 'bg-gradient-to-br from-red-50 to-white border-red-200';
      case 'de': return 'bg-gradient-to-br from-yellow-50 to-gray-50 border-yellow-200';
      default: return 'bg-gradient-to-br from-gray-50 to-gray-100';
    }
  };

  if (loading) {
    return (
      <MainLayout>
        <div className="p-6 space-y-6">
          <Skeleton className="h-12 w-64" />
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[1, 2, 3].map(i => <Skeleton key={i} className="h-64" />)}
          </div>
        </div>
      </MainLayout>
    );
  }

  if (error) {
    return (
      <MainLayout>
        <div className="p-6">
          <Card className="max-w-2xl mx-auto">
            <CardContent className="pt-12 pb-8 text-center">
              <Globe2 className="h-16 w-16 mx-auto text-gray-400 mb-4" />
              <h2 className="text-2xl font-bold mb-2">Unable to Load Global Modules</h2>
              <p className="text-muted-foreground mb-6">{error}</p>
              <Button onClick={fetchData}>Try Again</Button>
            </CardContent>
          </Card>
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <div className="p-6 space-y-6">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold bg-gradient-to-r from-blue-600 via-purple-600 to-red-600 bg-clip-text text-transparent">
              Global R-PAT Modules
            </h1>
            <p className="text-muted-foreground mt-1">
              Region-specific recruitment compliance training
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Badge variant="outline" className="text-sm">
              <Award className="h-4 w-4 mr-1" />
              {badges.length} Badge{badges.length !== 1 ? 's' : ''} Earned
            </Badge>
          </div>
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList>
            <TabsTrigger value="modules" className="flex items-center gap-2">
              <Globe2 className="h-4 w-4" />
              Modules
            </TabsTrigger>
            <TabsTrigger value="badges" className="flex items-center gap-2">
              <Trophy className="h-4 w-4" />
              My Badges
            </TabsTrigger>
          </TabsList>

          {/* Modules Tab */}
          <TabsContent value="modules" className="mt-6">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {modules.map((module) => {
                const progress = module.user_progress;
                const isCompleted = progress?.badge_earned;
                const progressPct = progress?.percentage || 0;
                
                return (
                  <Card 
                    key={module.id}
                    className={`relative overflow-hidden transition-all hover:shadow-lg cursor-pointer ${getRegionBg(module.id.replace('global-', ''))}`}
                    onClick={() => navigate(`/rpat/global/${module.id.replace('global-', '')}`)}
                    data-testid={`global-module-${module.id.replace('global-', '')}`}
                  >
                    {/* Flag Banner */}
                    <div className={`h-2 bg-gradient-to-r ${getRegionGradient(module.id.replace('global-', ''))}`} />
                    
                    <CardHeader className="pb-2">
                      <div className="flex items-start justify-between">
                        <div className="text-4xl">{module.flag_emoji}</div>
                        {isCompleted ? (
                          <Badge className="bg-emerald-500">
                            <CheckCircle2 className="h-3 w-3 mr-1" />
                            Certified
                          </Badge>
                        ) : progressPct > 0 ? (
                          <Badge variant="secondary">
                            {Math.round(progressPct)}% Complete
                          </Badge>
                        ) : null}
                      </div>
                      <CardTitle className="text-xl mt-2">{module.region}</CardTitle>
                      <CardDescription className="text-sm">
                        {module.description}
                      </CardDescription>
                    </CardHeader>
                    
                    <CardContent className="space-y-4">
                      {/* Stats */}
                      <div className="flex items-center gap-4 text-sm text-muted-foreground">
                        <span className="flex items-center gap-1">
                          <BookOpen className="h-4 w-4" />
                          {module.total_topics || 0} Topics
                        </span>
                        <span className="flex items-center gap-1">
                          <Clock className="h-4 w-4" />
                          {module.total_hours}h
                        </span>
                      </div>
                      
                      {/* Progress Bar */}
                      <div className="space-y-1">
                        <div className="flex justify-between text-xs text-muted-foreground">
                          <span>Progress</span>
                          <span>{Math.round(progressPct)}%</span>
                        </div>
                        <Progress value={progressPct} className="h-2" />
                      </div>
                      
                      {/* Badge Info */}
                      <div className="flex items-center justify-between pt-2 border-t">
                        <div className="flex items-center gap-2 text-sm">
                          <Award className="h-4 w-4 text-amber-500" />
                          <span className="font-medium">{module.badge_name}</span>
                        </div>
                        <ChevronRight className="h-5 w-5 text-muted-foreground" />
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>

            {modules.length === 0 && (
              <Card className="max-w-2xl mx-auto">
                <CardContent className="pt-12 pb-8 text-center">
                  <Globe2 className="h-16 w-16 mx-auto text-gray-400 mb-4" />
                  <h2 className="text-2xl font-bold mb-2">No Modules Available</h2>
                  <p className="text-muted-foreground mb-6">
                    Global modules are being prepared. Please check back later.
                  </p>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          {/* Badges Tab */}
          <TabsContent value="badges" className="mt-6">
            {badges.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {badges.map((badge) => (
                  <Card key={badge.id} className="overflow-hidden">
                    <div className={`h-2 bg-gradient-to-r ${badge.color || 'from-amber-500 to-yellow-500'}`} />
                    <CardContent className="pt-6 text-center">
                      <div className="text-5xl mb-4">{badge.flag_emoji}</div>
                      <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gradient-to-br from-amber-100 to-yellow-100 mb-4">
                        <Trophy className="h-8 w-8 text-amber-600" />
                      </div>
                      <h3 className="text-xl font-bold mb-1">{badge.badge_name}</h3>
                      <p className="text-sm text-muted-foreground mb-4">
                        {badge.badge_description}
                      </p>
                      <div className="space-y-2 text-sm">
                        <div className="flex items-center justify-center gap-2 text-muted-foreground">
                          <Shield className="h-4 w-4" />
                          <span className="font-mono">{badge.credential_id}</span>
                        </div>
                        <div className="flex items-center justify-center gap-2 text-muted-foreground">
                          <CheckCircle2 className="h-4 w-4 text-emerald-500" />
                          <span>Final Score: {badge.final_score}%</span>
                        </div>
                        <p className="text-xs text-muted-foreground">
                          Earned on {new Date(badge.earned_at).toLocaleDateString()}
                        </p>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            ) : (
              <Card className="max-w-2xl mx-auto">
                <CardContent className="pt-12 pb-8 text-center">
                  <Trophy className="h-16 w-16 mx-auto text-gray-300 mb-4" />
                  <h2 className="text-2xl font-bold mb-2">No Badges Yet</h2>
                  <p className="text-muted-foreground mb-6">
                    Complete global modules and pass their final assessments to earn region-specific badges.
                  </p>
                  <Button onClick={() => setActiveTab('modules')}>
                    <Globe2 className="mr-2 h-4 w-4" />
                    Explore Modules
                  </Button>
                </CardContent>
              </Card>
            )}
          </TabsContent>
        </Tabs>

        {/* Info Card */}
        <Card className="bg-gradient-to-r from-blue-50 via-purple-50 to-red-50 border-blue-200">
          <CardContent className="pt-6">
            <div className="flex items-start gap-4">
              <div className="p-3 rounded-lg bg-white shadow-sm">
                <Sparkles className="h-6 w-6 text-purple-600" />
              </div>
              <div>
                <h3 className="font-semibold mb-1">Earn Region-Specific Certifications</h3>
                <p className="text-sm text-muted-foreground">
                  Each global module covers region-specific employment law, visa processes, tax requirements, 
                  and compliance regulations. Complete all topics and pass the final assessment to earn your 
                  specialist badge and credential.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </MainLayout>
  );
};

export default RPATGlobalModules;
